import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    ArrowRight,
    ShieldAlert,
    Sparkles,
    Volume2,
    Turtle,
    CheckCircle2,
    Plus,
    Trophy,
    BookOpen,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { handlePaywallError } from "@/lib/access";
import { useQuotas } from "@/hooks/use-quotas";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import { useDashboard } from "@/contexts/DashboardContext";

function TTSMini({ text, language, testid }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(false);
    async function play(speed) {
        setBusy(true);
        try {
            const { data } = await api.post("/poc/tts", {
                text,
                language: ttsLanguageOf(language),
                speed,
                voice,
            });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    return (
        <div className="inline-flex gap-1">
            <button
                onClick={() => play("natural")}
                disabled={busy}
                data-testid={`${testid}-natural`}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-ink text-white hover:bg-emerald disabled:opacity-60"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
            </button>
            <button
                onClick={() => play("slow")}
                disabled={busy}
                data-testid={`${testid}-slow`}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-ink text-white hover:bg-emerald disabled:opacity-60"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Turtle className="w-3 h-3" />}
            </button>
        </div>
    );
}

/* -------------------- Catalog -------------------- */

function CatalogView({ tracks, onOpen, tracksLocked }) {
    const navigate = useNavigate();
    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="tracks-catalog">
            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Parcours professionnels</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
                Un métier. Une langue. Un vocabulaire exact.
            </h1>
            <p className="mt-3 text-sm text-inkSoft max-w-2xl">
                Chaque parcours est une série de modules ordonnés — vocabulaire technique, dialogues réalistes,
                pièges à éviter. Le parcours <span className="font-medium">Médical</span> est notre parcours vedette
                (12 modules).
            </p>

            {tracksLocked && (
                <div
                    className="mt-6 rounded-2xl border-2 border-ink bg-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
                    data-testid="tracks-locked-banner"
                >
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-6 h-6 text-terracotta" strokeWidth={1.75} />
                        <div>
                            <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft font-semibold">
                                Réservé aux abonnés
                            </p>
                            <p className="font-serif text-lg text-ink">
                                Les modules pro nécessitent un abonnement Lexia School.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/abonnement")}
                        data-testid="tracks-locked-upgrade"
                        className="md:ml-auto inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2.5 text-sm font-medium transition-colors"
                    >
                        S&apos;abonner
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tracks.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => onOpen(t.id)}
                        data-testid={`track-${t.id}`}
                        className={`text-left bg-white rounded-2xl p-5 border transition-all hover:shadow-sm ${
                            t.featured
                                ? "border-emerald ring-1 ring-emerald/20 hover:border-emerald"
                                : "border-lexiBorder hover:border-terracotta"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="text-2xl mb-1">{t.emoji}</div>
                                <h3 className="font-serif text-lg leading-tight">{t.title_fr}</h3>
                                <p className="text-xs text-inkSoft mt-1">{t.subtitle_fr}</p>
                                {t.featured && (
                                    <span className="mt-2 inline-block text-[10px] tracking-[0.22em] uppercase text-emerald font-semibold">
                                        Vedette
                                    </span>
                                )}
                            </div>
                            <ArrowRight className="w-4 h-4 text-inkSoft mt-1 shrink-0" />
                        </div>
                        {t.modules_completed > 0 && (
                            <p className="mt-3 text-[11px] text-emerald">
                                {t.modules_completed} module{t.modules_completed > 1 ? "s" : ""} terminé
                                {t.modules_completed > 1 ? "s" : ""}
                            </p>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

/* -------------------- Detail (module list) -------------------- */

function DetailView({ trackData, onOpenModule, onBack }) {
    const track = trackData.track;
    const modules = trackData.modules;
    const totalDone = trackData.progress.completed.length;
    const totalModules = trackData.progress.total;
    const trackComplete = totalModules > 0 && totalDone >= totalModules;
    const [cert, setCert] = useState(null);
    const [certBusy, setCertBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let ignore = false;
        (async () => {
            if (!trackComplete) return;
            try {
                const { data } = await api.get("/certificates/mine");
                if (ignore) return;
                const found = (data.certificates || []).find((c) => c.track_id === track.id);
                if (found) setCert(found);
            } catch (err) {
                // Certificate lookup is a soft dependency — a network failure
                // here just leaves the CTA in "generate" mode. Log for triage.
                console.warn("[Parcours] certificates/mine lookup failed:", err);
            }
        })();
        return () => { ignore = true; };
    }, [track.id, trackComplete]);

    async function generateCertificate() {
        setCertBusy(true);
        try {
            const { data } = await api.post(`/certificates/${track.id}/generate`);
            setCert(data.certificate);
            toast.success("Certificat prêt.");
            navigate(`/certificats/${data.certificate.cert_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCertBusy(false);
        }
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid={`track-detail-${track.id}`}>
            <button
                onClick={onBack}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="track-back"
            >
                <ArrowLeft className="w-4 h-4" /> Catalogue
            </button>
            <div className="flex items-start gap-3">
                <div className="text-4xl">{track.emoji}</div>
                <div className="min-w-0">
                    <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">Parcours professionnel</p>
                    <h1 className="font-serif text-3xl md:text-4xl leading-tight">{track.title_fr}</h1>
                    <p className="mt-1 text-sm text-inkSoft">{track.subtitle_fr}</p>
                </div>
            </div>

            {track.disclaimer_fr && (
                <div
                    className="mt-6 flex items-start gap-3 rounded-2xl border border-terracotta/40 bg-terracotta/8 px-4 py-3"
                    data-testid="track-disclaimer"
                >
                    <ShieldAlert className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
                    <p className="text-sm text-ink font-medium leading-snug">{track.disclaimer_fr}</p>
                </div>
            )}

            <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                {totalDone} / {trackData.progress.total} modules terminés
            </p>

            {trackComplete && (
                <div
                    className="mt-4 rounded-2xl border border-emerald bg-emerald/8 p-5 flex items-start gap-4"
                    data-testid="track-certificate-cta"
                >
                    <div className="w-11 h-11 rounded-xl bg-emerald/20 flex items-center justify-center shrink-0">
                        <Trophy className="w-5 h-5 text-emerald" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-emerald font-semibold">
                            Parcours complet
                        </p>
                        <p className="font-serif text-lg mt-0.5">
                            Vous avez validé tous les modules — voici votre certificat.
                        </p>
                        <p className="text-[11px] text-inkSoft mt-1">
                            Téléchargeable en PNG / PDF, avec un lien public de vérification.
                        </p>
                    </div>
                    {cert ? (
                        <button
                            onClick={() => navigate(`/certificats/${cert.cert_id}`)}
                            data-testid="track-view-certificate"
                            className="inline-flex items-center gap-2 bg-emerald text-white hover:bg-emerald/90 rounded-full px-4 py-2 text-sm shrink-0 transition-colors"
                        >
                            <Trophy className="w-4 h-4" /> Voir le certificat
                        </button>
                    ) : (
                        <button
                            onClick={generateCertificate}
                            disabled={certBusy}
                            data-testid="track-generate-certificate"
                            className="inline-flex items-center gap-2 bg-emerald text-white hover:bg-emerald/90 rounded-full px-4 py-2 text-sm shrink-0 disabled:opacity-60 transition-colors"
                        >
                            {certBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                            Obtenir mon certificat
                        </button>
                    )}
                </div>
            )}

            <ul className="mt-3 space-y-2">
                {modules.map((m) => (
                    <li key={m.index}>
                        <button
                            onClick={() => onOpenModule(m.index)}
                            data-testid={`module-${m.index}`}
                            className="w-full text-left flex items-start justify-between gap-3 bg-white border border-lexiBorder rounded-2xl p-4 hover:border-terracotta transition-colors"
                        >
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono text-inkSoft">M{String(m.index + 1).padStart(2, "0")}</span>
                                    <span className="font-serif text-base">{m.title_fr}</span>
                                </div>
                                <p className="text-[11px] text-inkSoft mt-0.5">{m.focus_fr}</p>
                            </div>
                            <span
                                className={`text-[10px] tracking-[0.22em] uppercase font-semibold shrink-0 self-center ${
                                    m.status === "completed"
                                        ? "text-emerald"
                                        : m.status === "in_progress"
                                          ? "text-terracotta"
                                          : "text-inkSoft"
                                }`}
                            >
                                {m.status === "completed" ? "Terminé" : m.status === "in_progress" ? "En cours" : "À commencer"}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* -------------------- Module lesson view -------------------- */

function ModuleView({ trackId, moduleIndex, moduleMeta, content, languageFr, onBack, onCompleted }) {
    const [answers, setAnswers] = useState(() =>
        new Array((content?.practice_questions || []).length).fill(null),
    );
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [addedVocab, setAddedVocab] = useState(false);
    const { refresh: refreshDashboard } = useDashboard();

    async function submitPractice() {
        if (answers.some((a) => a === null)) {
            toast.error("Répondez à toutes les questions.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(
                `/tracks/${trackId}/module/${moduleIndex}/practice`,
                { answers: answers.map((a) => Number(a)) },
            );
            setResult(data);
            if (data.score >= 60) onCompleted?.(moduleIndex);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    async function addVocab() {
        try {
            const { data } = await api.post(
                `/tracks/${trackId}/module/${moduleIndex}/add_to_reviews`,
                { words: content.vocabulary || [] },
            );
            toast.success(`${data.added} mots ajoutés à vos révisions.`);
            setAddedVocab(true);
            refreshDashboard?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid={`track-module-${moduleIndex}`}>
            <button
                onClick={onBack}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="module-back"
            >
                <ArrowLeft className="w-4 h-4" /> Modules
            </button>

            <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">
                Module {String(moduleIndex + 1).padStart(2, "0")} · {trackId}
            </p>
            <h1 className="font-serif text-2xl md:text-3xl mt-1 leading-tight">{moduleMeta.title_fr}</h1>
            <p className="mt-1 text-sm text-inkSoft">{moduleMeta.focus_fr}</p>

            {/* Vocabulary */}
            {(content.vocabulary || []).length > 0 && (
                <section className="mt-8" data-testid="module-vocab">
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Vocabulaire</p>
                        <button
                            onClick={addVocab}
                            disabled={addedVocab}
                            data-testid="module-add-vocab"
                            className="inline-flex items-center gap-1 text-xs bg-terracotta hover:bg-terracottaDark text-white rounded-full px-3 py-1.5 disabled:opacity-60"
                        >
                            <Plus className="w-3 h-3" />
                            {addedVocab ? "Ajouté" : "Ajouter aux révisions"}
                        </button>
                    </div>
                    <ul className="mt-3 space-y-2">
                        {content.vocabulary.map((v, i) => (
                            <li key={i} className="bg-white border border-lexiBorder rounded-xl p-3 text-sm">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-serif text-base">{v.word}</span>
                                            <span className="text-inkSoft">— {v.translation_fr}</span>
                                            {v.ipa && <span className="text-[11px] text-inkSoft">{v.ipa}</span>}
                                            {v.register && (
                                                <span className="text-[9px] tracking-[0.2em] uppercase text-emerald">
                                                    {v.register}
                                                </span>
                                            )}
                                        </div>
                                        {v.natural_sentence && (
                                            <p className="text-[11px] text-inkSoft italic mt-1">
                                                « {v.natural_sentence} » — {v.sentence_translation_fr}
                                            </p>
                                        )}
                                    </div>
                                    <TTSMini text={v.word} language={languageFr} testid={`vocab-${i}-tts`} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Dialogue */}
            {content.dialogue_target && (
                <section className="mt-8" data-testid="module-dialogue">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-2">Dialogue professionnel</p>
                    <div className="bg-white border border-lexiBorder rounded-2xl p-4">
                        <p className="font-serif whitespace-pre-wrap leading-relaxed">{content.dialogue_target}</p>
                        <div className="mt-3 pt-3 border-t border-lexiBorder">
                            <p className="text-xs text-inkSoft italic whitespace-pre-wrap">
                                {content.dialogue_translation_fr}
                            </p>
                        </div>
                        <div className="mt-3">
                            <TTSMini text={content.dialogue_target} language={languageFr} testid="dialogue-tts" />
                        </div>
                    </div>
                </section>
            )}

            {/* Domain note */}
            {content.domain_note_fr && (
                <p className="mt-6 text-[11px] text-inkSoft italic" data-testid="module-domain-note">
                    Note métier : {content.domain_note_fr}
                </p>
            )}

            {/* Cultural tips */}
            {(content.cultural_tips_fr || []).length > 0 && (
                <section className="mt-6" data-testid="module-cultural">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-2">Conseils culturels</p>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                        {content.cultural_tips_fr.map((t, i) => (
                            <li key={i}>{t}</li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Practice */}
            {(content.practice_questions || []).length > 0 && (
                <section className="mt-8" data-testid="module-practice">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-2">Pratique</p>
                    <ul className="space-y-3">
                        {content.practice_questions.map((q, i) => (
                            <li key={i} className="bg-white border border-lexiBorder rounded-2xl p-4">
                                <p className="font-serif" data-testid={`module-q-${i}`}>
                                    {i + 1}. {q.prompt_fr}
                                </p>
                                <div className="mt-2 space-y-1.5">
                                    {(q.choices_target || []).map((c, j) => {
                                        const isSelected = answers[i] === j;
                                        const graded = result?.graded?.[i];
                                        const isCorrect = graded && j === Number(graded.correct_index);
                                        const isWrong = graded && isSelected && !graded.correct;
                                        return (
                                            <button
                                                key={j}
                                                onClick={() => {
                                                    if (result) return;
                                                    const copy = [...answers];
                                                    copy[i] = j;
                                                    setAnswers(copy);
                                                }}
                                                disabled={!!result}
                                                data-testid={`module-q-${i}-choice-${j}`}
                                                className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition-colors ${
                                                    isCorrect
                                                        ? "bg-emerald text-white border-emerald"
                                                        : isWrong
                                                          ? "bg-terracotta/10 text-terracotta border-terracotta/30"
                                                          : isSelected
                                                            ? "bg-ink text-white border-ink"
                                                            : "bg-paperAlt border-lexiBorder hover:bg-paper"
                                                }`}
                                            >
                                                {c}
                                            </button>
                                        );
                                    })}
                                </div>
                                {result?.graded?.[i]?.explanation_fr && (
                                    <p className="mt-2 text-xs text-inkSoft italic">
                                        {result.graded[i].explanation_fr}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                    {!result ? (
                        <button
                            onClick={submitPractice}
                            disabled={busy}
                            data-testid="module-practice-submit"
                            className="mt-4 inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm disabled:opacity-60 transition-colors"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Corriger
                        </button>
                    ) : (
                        <p className="mt-4" data-testid="module-practice-score">
                            Score : <span className="font-serif text-2xl text-emerald">{result.score}</span> /100
                            {result.score >= 60 && (
                                <span className="ml-3 inline-flex items-center gap-1 text-emerald">
                                    <Trophy className="w-4 h-4" />
                                    Module validé.
                                </span>
                            )}
                        </p>
                    )}
                </section>
            )}
        </div>
    );
}

/* -------------------- Main -------------------- */

export default function Parcours() {
    const [tracks, setTracks] = useState([]);
    const [openTrackId, setOpenTrackId] = useState(null);
    const [trackData, setTrackData] = useState(null);
    const [openModule, setOpenModule] = useState(null);
    const [moduleContent, setModuleContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyModule, setBusyModule] = useState(false);
    const [languageFr, setLanguageFr] = useState("");
    const bootstrapped = useRef(false);
    const { trackId: urlTrackId } = useParams();
    const navigate = useNavigate();
    const { data: quotas } = useQuotas();
    const tracksLocked = !!quotas?.features?.tracks?.locked;

    useEffect(() => {
        if (bootstrapped.current) return;
        bootstrapped.current = true;
        (async () => {
            try {
                const [{ data }, { data: prof }] = await Promise.all([
                    api.get("/tracks"),
                    api.get("/profile/me"),
                ]);
                setTracks(data.tracks);
                setLanguageFr(prof.profile?.target_language || "");
                if (urlTrackId) {
                    await loadTrack(urlTrackId);
                }
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — `loadTrack` here is a closure that
        // captures `urlTrackId`; the effect only runs once thanks to the
        // `bootstrapped` ref, and the URL param drives subsequent nav via
        // route re-mount, not deps.
    }, []);

    async function loadTrack(id) {
        try {
            const { data } = await api.get(`/tracks/${id}`);
            setTrackData(data);
            setOpenTrackId(id);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function openModuleLesson(idx) {
        setBusyModule(true);
        try {
            const { data } = await api.post(`/tracks/${openTrackId}/module/${idx}/lesson`);
            setModuleContent(data);
            setOpenModule(idx);
        } catch (err) {
            if (!handlePaywallError(err)) toast.error(formatApiError(err));
        } finally {
            setBusyModule(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="tracks-loading">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    if (openTrackId && openModule !== null && moduleContent) {
        const mMeta = trackData?.modules?.[openModule];
        return (
            <ModuleView
                trackId={openTrackId}
                moduleIndex={openModule}
                moduleMeta={mMeta || {}}
                content={moduleContent.content}
                languageFr={languageFr}
                onBack={() => {
                    setOpenModule(null);
                    setModuleContent(null);
                    loadTrack(openTrackId);
                }}
                onCompleted={() => loadTrack(openTrackId)}
            />
        );
    }

    if (busyModule) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
                <p className="mt-3 text-sm text-inkSoft">Préparation du module…</p>
            </div>
        );
    }

    if (openTrackId && trackData) {
        return (
            <DetailView
                trackData={trackData}
                onOpenModule={openModuleLesson}
                onBack={() => {
                    setOpenTrackId(null);
                    setTrackData(null);
                    navigate("/parcours", { replace: true });
                }}
            />
        );
    }

    return <CatalogView tracks={tracks} tracksLocked={tracksLocked} onOpen={(id) => {
        navigate(`/parcours/${id}`);
        loadTrack(id);
    }} />;
}
